import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { PrintOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { Fragment } from "react"

import { InventoryLotStack, InventoryProductDescription, InventoryProductIdWithVersion } from "../../../../components"
import { formatInventoryLocation } from "../../../../utils"
import type { TransferPalletWithProduct } from "../../types"
import {
  calculateTransferTotals,
  formatPalletDestination,
  isTransferAdjustmentUpserted,
  isTransferWorkOrderUpserted,
} from "../../utils/transfer"

import { TransferLotSyncStatusChip } from "./TransferLotSyncStatusChip"

import { type Transfer, TransferType } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

interface TransferPalletsTableProps {
  transfer: Transfer
  isPrinting: boolean
  onPalletLabelPrint: (pallet: TransferPalletWithProduct) => void
}

const BASE_COLUMN_COUNT = 7
const INTER_WAREHOUSE_COLUMN_COUNT = 2
const INTRA_WAREHOUSE_COLUMN_COUNT = 1
const PRINT_COLUMN_COUNT = 1

export const TransferPalletsTable: FC<TransferPalletsTableProps> = ({ transfer, isPrinting, onPalletLabelPrint }) => {
  const { uomName } = calculateTransferTotals(transfer.products)
  const isInterWarehouse = transfer.transferType === TransferType.InterWarehouse
  const canPrintPalletLabel = isTransferWorkOrderUpserted(transfer) || isTransferAdjustmentUpserted(transfer)
  const columnCount =
    BASE_COLUMN_COUNT +
    (isInterWarehouse ? INTER_WAREHOUSE_COLUMN_COUNT : INTRA_WAREHOUSE_COLUMN_COUNT) +
    (canPrintPalletLabel ? PRINT_COLUMN_COUNT : 0)

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Product ID</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Reference #</TableCell>
            {isInterWarehouse && <TableCell>LPN</TableCell>}
            <TableCell>Current Location</TableCell>
            <TableCell>Lot #</TableCell>
            <TableCell>Batch #</TableCell>
            <TableCell>Qty to Move</TableCell>
            {isInterWarehouse ? <TableCell>Status</TableCell> : <TableCell>New Location</TableCell>}
            {canPrintPalletLabel && <TableCell>Print QR Code</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {transfer.products.map((transferProduct) => {
            const totals = calculateTransferTotals([transferProduct])
            const pallets = transferProduct.pallets.length > 0 ? transferProduct.pallets : [undefined]

            return (
              <Fragment key={transferProduct.productId}>
                {pallets.map((pallet) => (
                  <TableRow
                    key={pallet?.referenceNbr ?? `product-${transferProduct.productId}`}
                    className='align-top'
                    data-testid={`transfer.pallets-table.row-${pallet?.referenceNbr ?? `product-${transferProduct.productId}`}`}
                  >
                    <TableCell>
                      <InventoryProductIdWithVersion product={transferProduct.product} />
                    </TableCell>
                    <TableCell>
                      <InventoryProductDescription product={transferProduct.product} />
                    </TableCell>
                    <TableCell>{pallet?.referenceNbr ?? ""}</TableCell>
                    {isInterWarehouse && <TableCell>{pallet?.lpn ?? ""}</TableCell>}
                    <TableCell>{formatInventoryLocation(pallet?.sourceLocation)}</TableCell>
                    <TableCell>
                      <InventoryLotStack
                        lots={pallet?.lots}
                        getKey={(lot) => lot.transferLotId}
                        render={(lot) => lot.rawInventory?.lotNbr ?? ""}
                      />
                    </TableCell>
                    <TableCell>
                      <InventoryLotStack
                        lots={pallet?.lots}
                        getKey={(lot) => lot.transferLotId}
                        render={(lot) => lot.rawInventory?.batchNbr ?? ""}
                      />
                    </TableCell>
                    <TableCell>
                      <InventoryLotStack
                        lots={pallet?.lots}
                        getKey={(lot) => lot.transferLotId}
                        render={(lot) =>
                          isNil(lot.qtyToMove) ? "" : `${formatNumber(lot.qtyToMove)} ${uomName ?? ""}`
                        }
                      />
                    </TableCell>
                    {isInterWarehouse ? (
                      <TableCell>
                        <InventoryLotStack
                          lots={pallet?.lots}
                          getKey={(lot) => lot.transferLotId}
                          render={(lot) => <TransferLotSyncStatusChip status={lot.as400SyncStatus} />}
                        />
                      </TableCell>
                    ) : (
                      <TableCell>{formatPalletDestination(pallet)}</TableCell>
                    )}
                    {canPrintPalletLabel && (
                      <TableCell>
                        {pallet && (
                          <IconButton
                            size='small'
                            disabled={isPrinting}
                            onClick={() => onPalletLabelPrint({ ...pallet, product: transferProduct.product })}
                            data-testid='transfer.pallets-table.print-pallet-label-button'
                          >
                            <PrintOutlined />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow data-testid={`transfer.pallets-table.totals-row-${transferProduct.productId}`}>
                  <TableCell colSpan={columnCount}>
                    <div className='flex items-center justify-end gap-x-6 font-medium'>
                      <span>Total Pallets: {totals.palletCount}</span>
                      <span>
                        Total UoM: {formatNumber(totals.quantity)} {uomName ?? ""}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
